import {
  DevHubAutocomplete,
  DevHubFormControl,
  DevHubTextField,
} from '@devxdiscover/devhub-ui';
import { useEffect, useState } from 'react';

import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder';
import { useGroupsByLocation } from '../../services/github';
import { scmIntegrationsApiRef } from '@backstage/integration-react';
import { useApi } from '@backstage/core-plugin-api';

import { parseRepoPickerUrl } from '../../util/entityUtils';

export type GroupEntityPickerOptions = FieldExtensionComponentProps<string>;

/**
 * The underlying component that is rendered in the form for the `GroupEntityPicker`
 * field extension.
 * @param props GroupEntityPickerOptions which returns string and doesn't accept any ui-options
 * @public
 */
export const GroupEntityPicker = (props: GroupEntityPickerOptions) => {
  const {
    onChange,
    schema: { title = 'Group', description = 'An group from the catalog' },
    required,
    rawErrors,
    formData,
    idSchema,
  } = props;
  const integrationApi = useApi(scmIntegrationsApiRef);
  const hosts = integrationApi.github
    .list()
    .map(integration => integration.config.host);
  const formContext = props.formContext;
  const { value: groupsValue, loading, error } = useGroupsByLocation(hosts);
  const [groups, setGroups] = useState<string[]>([]);
  useEffect(() => {
    const repoUrl = formContext?.formData?.repoUrl;
    if (repoUrl) {
      const { host } = parseRepoPickerUrl(repoUrl);
      if (host) {
        setGroups(groupsValue?.get(host) ?? []);
      }
    }
    const host = formContext?.formData?.host;
    if (host) {
      setGroups(groupsValue?.get(host) ?? []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupsValue, loading, error, formContext?.formData]);

  // Update the current entity onSelect
  const onSelect = (_: any, entity: string | null) => {
    onChange(entity || '');
  };

  return (
    <DevHubFormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !formData}
    >
      <DevHubAutocomplete
        id={idSchema?.$id}
        value={formData as string}
        loading={loading}
        onChange={onSelect}
        options={groups || []}
        autoSelect
        renderInput={params => (
          <DevHubTextField
            {...params}
            label={title}
            margin="normal"
            helperText={description}
            variant="outlined"
            required={required}
            InputProps={params.InputProps}
          />
        )}
      />
    </DevHubFormControl>
  );
};
